/**
 * AuditTraceButton — "send the whole run off for a full audit".
 *
 * WHY: the quote brain teaches itself one event at a time (quote_brain_events →
 * quote-teach proposals). Nothing ever looked at a WHOLE run — start assessment
 * → scope chat → convert → draft → refines → hand edits → takeoff — and asked
 * what the engine kept getting wrong. This button captures exactly that: every
 * event since the assessment opened, every material and where its price came
 * from, every line the matcher abstained on, and the diff between the AI's
 * first draft and what the quote says now.
 *
 * It rides inside FlowStrip, so it appears on EVERY stage (assessment, backend
 * quote, takeoff, job) with the anchor that page already knows — hit it wherever
 * you are and the trace runs from the start of the workflow to that point.
 *
 * Owner-tier: the bundle carries costs, margins and supplier pricing for the
 * whole job. The client gate here is cosmetic — api/quote-audit.js requires an
 * admin JWT.
 */
import { useState } from "react";
import { apiFetch } from "../lib/apiFetch";

const C = {
  surface: "#161a20", card: "#1c2028", border: "#2a3140",
  lime: "#a5ce3a", text: "#e8eaf0", muted: "#8892a4", warn: "#e2b93b", danger: "#e5484d",
};

// Prompts, not categories — each one nudges toward the specific detail that
// makes the trace actionable ("which line", "what should it have been").
const QUICK = [
  "A material didn't come up for price at all — ",
  "A material priced wrong — should have been ",
  "The matcher picked the wrong product for ",
  "Labour hours were off — should have been ",
  "It missed scope that was in the assessment: ",
  "It included something it shouldn't have: ",
];

const STAGE_LABEL = {
  assessment: "assessment", quote: "backend quote", sheet: "takeoff", job: "job", request: "request",
};

export default function AuditTraceButton({ anchor, stage }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { ref, headline, emailed, markdown }
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(false);

  const anchorKey = anchor ? Object.keys(anchor).find((k) => anchor[k]) : null;
  if (!anchorKey) return null;

  // /api/quote-audit takes the same anchor names /api/flow does.
  const BODY_KEY = { assessment: "assessmentId", quote: "quoteId", sheet: "sheetId", job: "jobNum", request: "requestId" };

  async function capture() {
    setBusy(true); setErr(null); setResult(null);
    try {
      const r = await apiFetch("/api/quote-audit", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "capture",
          stage: stage || STAGE_LABEL[anchorKey] || anchorKey,
          note: note.trim() || null,
          [BODY_KEY[anchorKey]]: anchor[anchorKey],
        }),
      });
      const j = await r.json();
      if (!r.ok || j.ok === false) throw new Error(j.error || `HTTP ${r.status}`);
      setResult(j);
    } catch (e) {
      setErr(e.message);
    } finally { setBusy(false); }
  }

  async function copyReport() {
    try {
      await navigator.clipboard.writeText(result?.markdown || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch { setErr("Copy failed — the report is in your email either way."); }
  }

  function close() {
    setOpen(false); setNote(""); setResult(null); setErr(null); setCopied(false);
  }

  const h = result?.headline || {};

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title="Capture this whole workflow — every event, price decision and hand edit since the assessment opened — and send it for a full audit"
        style={{
          flexShrink: 0, alignSelf: "center", marginLeft: 6, padding: "6px 10px", borderRadius: 8,
          background: C.card, border: `1px solid ${C.border}`, color: C.muted,
          fontSize: 11, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase",
          cursor: "pointer", whiteSpace: "nowrap", fontFamily: "'Roboto', sans-serif",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(165,206,58,.5)"; e.currentTarget.style.color = C.lime; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.muted; }}
      >
        🔬 Audit
      </button>

      {open && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) close(); }}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.75)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
        >
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, width: "100%", maxWidth: 640, maxHeight: "88vh", overflowY: "auto", fontFamily: "'Roboto', sans-serif", color: C.text }}>
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 17, fontWeight: 700 }}>Send this workflow for a full audit</div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 4, lineHeight: 1.5 }}>
                Captures everything from the moment the assessment opened up to this point —
                the scope chat, every draft and refine with the instructions given, every material
                and exactly where its price came from, every line the matcher wouldn't commit to,
                and the diff between the AI's first draft and what the quote says now.
                Nothing is changed; it's a read.
              </div>
            </div>

            {!result ? (
              <div style={{ padding: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 6 }}>
                  What went wrong here? <span style={{ fontWeight: 400 }}>(optional — the trace is worth sending either way)</span>
                </div>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={5}
                  placeholder="e.g. The 200A meter socket never priced — it's in the WESCO list. And it quoted 6h for a mast swap that's always 10h."
                  style={{
                    width: "100%", boxSizing: "border-box", background: C.card, color: C.text,
                    border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, fontSize: 13,
                    fontFamily: "inherit", resize: "vertical", minHeight: 90,
                  }}
                />
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                  {QUICK.map((q) => (
                    <button
                      key={q}
                      onClick={() => setNote((n) => (n ? n.replace(/\s*$/, "\n") : "") + q)}
                      style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 999, color: C.muted, fontSize: 11, padding: "4px 10px", cursor: "pointer" }}
                    >
                      + {q.replace(/ — .*$/, "").replace(/:\s*$/, "").trim()}
                    </button>
                  ))}
                </div>
                {err && <div style={{ marginTop: 10, fontSize: 12, color: C.danger }}>⚠ {err}</div>}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
                  <button onClick={close} style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8, color: C.muted, padding: "8px 14px", fontSize: 13, cursor: "pointer" }}>Cancel</button>
                  <button
                    onClick={capture}
                    disabled={busy}
                    style={{ background: busy ? C.card : C.lime, border: "none", borderRadius: 8, color: busy ? C.muted : "#10140c", padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: busy ? "default" : "pointer" }}
                  >
                    {busy ? "Assembling the trace…" : "Capture & send →"}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ padding: 16 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.lime }}>Trace #{result.ref} captured</div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
                  {result.emailed
                    ? "Emailed to the owner inbox."
                    : `⚠ Stored, but the email failed${result.emailError ? ` (${result.emailError})` : ""} — the trace itself is safe.`}
                </div>

                <div style={{ marginTop: 12, background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, fontSize: 12.5, lineHeight: 1.7 }}>
                  <div><b style={{ color: h.not_priced ? C.warn : C.text }}>{h.not_priced ?? 0}</b> material lines never priced from the book <span style={{ color: C.muted }}>({h.pct_unpriced ?? 0}% of {h.materials ?? 0})</span></div>
                  <div><b style={{ color: h.suspect ? C.warn : C.text }}>{h.suspect ?? 0}</b> priced but flagged as suspect</div>
                  <div><b>{h.abstentions ?? 0}</b> lines the matcher found candidates for and wouldn't commit</div>
                  <div><b>{h.hand_edits ?? 0}</b> hand corrections to the AI's first draft</div>
                  <div><b>{h.events ?? 0}</b> workflow events captured</div>
                </div>

                <div style={{ marginTop: 12, fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
                  To have Claude work it: say <b style={{ color: C.text }}>“audit trace {result.ref}”</b> in the ElecBid chat —
                  or hit Copy and paste the whole report straight in.
                </div>

                {err && <div style={{ marginTop: 10, fontSize: 12, color: C.danger }}>⚠ {err}</div>}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
                  <button onClick={copyReport} style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8, color: copied ? C.lime : C.muted, padding: "8px 14px", fontSize: 13, cursor: "pointer" }}>
                    {copied ? "✓ Copied" : "Copy full report"}
                  </button>
                  <button onClick={close} style={{ background: C.lime, border: "none", borderRadius: 8, color: "#10140c", padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Done</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
